<template>
  <ul class="default-tip-input">
    <li v-for="(tipOption, index) in tipOptions" :key="index" class="default-tip-input__list-item">
      <button
        :id="`default-tip-input-${tipOption.id}`"
        :class="[
          'default-tip-input__btn',
          'iw-body-text-small',
          { 'default-tip-input__btn--selected': valueSelected === tipOption.id }
        ]"
        :disabled="isCartEmpty"
        :aria-pressed="valueSelected === tipOption.id"
        @click="onButtonClick(tipOption)"
      >
        {{ tipOption.text }}
        <label class="iw-visuallyhidden" :for="`default-tip-input-${tipOption.id}`">Tip of {{ tipOption.text }}</label>
      </button>
    </li>
    <li class="default-tip-input__list-item">
      <input
        id="default-tip-input__input"
        v-model="input"
        aria-label="Tip Total"
        placeholder="$0.00"
        type="text"
        :class="['default-tip-input__input', { 'default-tip-input__input--selected': valueSelected === 'input' }]"
        :disabled="isCartEmpty"
        @blur="isInputActive = false"
        @focus="onInputFocus"
        @update:modelValue="selectTipPercentage(-1)"
      />
    </li>
  </ul>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { DEFAULT_TIP, MAPI_API_TYPE } from "../../../../../../app-constants";

export default {
  name: "DefaultTipInput",
  props: {
    modelValue: Number
  },
  data: function () {
    return {
      valueSelected: null,
      isInputActive: false,
      tipOptions: [
        { id: "btn_0", text: "No Tip", value: "noTip" },
        { id: "btn_1", text: "15%", value: 15 },
        { id: "btn_2", text: "18%", value: 18 },
        { id: "btn_3", text: "20%", value: 20 }
      ]
    };
  },
  computed: {
    ...mapGetters({
      getTipPercentage: "cart/getTipPercentage",
      getCartSize: "cart/getCartSize",
      getCartItems: "cart/getCartItems",
      getSubTotal: "cart/getSubTotal",
      getOAPITotalDiscounts: "cart/getOAPITotalDiscounts",
      APIType: "venueSettings/APIType"
    }),
    subTotal() {
      if (this.APIType === MAPI_API_TYPE) {
        return this.getSubTotal(this.getCartItems);
      }
      //OAPI Total discounts is expressed as negative, then we subtract a negative amount from the total
      return this.getSubTotal(this.getCartItems) - this.getOAPITotalDiscounts;
    },
    tip() {
      return ((this.subTotal * this.getTipPercentage) / 100).toFixed(2).toString();
    },
    input: {
      get: function () {
        const value = this.isInputActive
          ? this.modelValue.toString()
          : "$" + this.modelValue.toFixed(2).replace(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, "$1,");
        return parseFloat(value.replace("$", "")) > 0 ? value : "";
      },
      set: function (modifiedValue) {
        let newValue = parseFloat(modifiedValue.replace(/[^\d.]/g, ""));

        if (isNaN(newValue)) {
          newValue = 0;
        }

        this.$emit("update:modelValue", newValue);
      }
    },
    isCartEmpty() {
      return this.getCartSize === 0;
    }
  },
  watch: {
    subTotal: function () {
      if (this.getTipPercentage > 0) {
        this.setTip(this.getTipPercentage);
      }
    }
  },
  created() {
    if (this.getTipPercentage > 0 || this.getTipPercentage === "noTip") {
      this.onloadValueSelected(this.getTipPercentage);
    } else if (this.getTipPercentage === 0 && this.getTipPercentage != "noTip") {
      this.onloadValueSelected(DEFAULT_TIP);
    }
  },
  methods: {
    ...mapActions({
      selectTipPercentage: "cart/selectTipPercentage"
    }),
    setTip(tipPercentage) {
      this.selectTipPercentage(tipPercentage);
      this.input = this.tip;
    },
    onInputFocus() {
      this.valueSelected = "input";
      this.isInputActive = true;
    },
    onButtonClick(tipOption) {
      this.valueSelected = tipOption.id;
      this.setTip(tipOption.value);
    },
    onloadValueSelected(onLoadValue) {
      const optionSelected = this.tipOptions.filter((option) => option.value == onLoadValue);
      this.valueSelected = optionSelected[0].id;
      this.setTip(onLoadValue);
    }
  }
};
</script>

<style scoped lang="scss">
.default-tip-input {
  width: 100%;
  display: flex;
  align-content: center;
  align-items: flex-start;
  padding-inline-start: 0px;

  &__list-item {
    display: block;
    height: 46px;
    width: calc(20% + 1px);
    margin-left: -1px;
  }

  &__input {
    height: 100%;
    width: 100%;
    box-sizing: border-box;
    text-align: center;
    outline: none;
    border: 1px solid $group-3-color-3-alpha-40;
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
    font-size: 14px;
    font-family: $group-3-font-family-1;
    color: $group-3-color-2;
    background-color: $group-3-color-1;
    z-index: 1;

    &--selected {
      border: solid 2px $group-3-color-4;
      border-radius: 3px;
      color: $group-3-color-2;
    }
  }

  &__btn {
    height: 100%;
    width: 100%;
    border: solid 1px $group-3-color-3-alpha-40;
    background-color: #f6f7f8;
    outline: none;
    cursor: pointer;

    &:first-child {
      border-top-left-radius: 3px;
      border-bottom-left-radius: 3px;
    }

    @include hover-supported() {
      &:hover {
        background-color: $group-3-color-4-alpha-04;
      }
    }

    &:active {
      background-color: rgba(black, 0.1);
      color: $group-3-color-2;
    }

    &--selected {
      transition: background-color 0.5s;
      background-color: $group-3-color-4;
      border: solid 1px $group-3-color-4;
      color: $group-3-color-1;
      border: 1px;
      z-index: 1;

      @include hover-supported() {
        &:hover {
          background-color: $group-3-color-4;
        }
      }

      &:active {
        background-color: $group-3-color-4;
        color: $group-3-color-1;
        opacity: 1;
      }
    }
  }
}
</style>
